import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';

import { cn } from '../../lib/utils';
import { Input } from './input';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export interface SearchableSelectInDialogOption {
  id: string | number;
  name: string;
  searchText?: string;
}

interface SearchableSelectInDialogProps {
  value: string | undefined;
  onValueChange: (value: string) => void;
  placeholder: string;
  searchPlaceholder: string;
  options: SearchableSelectInDialogOption[];
  disabled?: boolean;
  loading?: boolean;
  loadingText?: string;
  className?: string;
}

/**
 * Searchable dropdown matching SearchableSelect visuals, safe inside Radix Dialog
 * (Popover modal={false} — no portaled Select / focus-trap conflicts).
 */
export function SearchableSelectInDialog({
  value,
  onValueChange,
  placeholder,
  searchPlaceholder,
  options,
  disabled = false,
  loading = false,
  loadingText = 'Loading...',
  className,
}: SearchableSelectInDialogProps) {
  const isDisabled = disabled || loading;
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const filteredOptions = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return options.filter((option) => {
      const haystack = (option.searchText ?? option.name ?? '').toLowerCase();
      const matchesSearch = !q || haystack.includes(q);
      const isSelected = value != null && option.name === value;
      return matchesSearch || isSelected;
    });
  }, [options, searchTerm, value]);

  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      return;
    }
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  const displayLabel = value?.trim() ? value : null;

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild disabled={isDisabled}>
        <button
          type="button"
          disabled={isDisabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-md border border-[#E2E5EA] bg-white px-3 py-2 text-[13px] text-left transition-colors',
            'hover:border-heritage-grey focus:outline-none focus:ring-2 focus:ring-hero-red/30 focus:border-hero-red',
            'disabled:cursor-not-allowed disabled:opacity-50',
            open && 'border-hero-red ring-2 ring-hero-red/30',
            className
          )}
        >
          <span className={cn('truncate', !displayLabel && 'text-heritage-grey')}>
            {loading ? loadingText : displayLabel ?? placeholder}
          </span>
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 ml-2 text-heritage-grey transition-transform',
              open && 'rotate-180'
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="z-[100] w-[var(--radix-popover-trigger-width)] p-0 border-[#E2E5EA] shadow-lg"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="p-2 border-b border-[#E2E5EA]">
          <Input
            ref={searchRef}
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Escape') {
                setOpen(false);
                setSearchTerm('');
              }
              if (e.key === 'Enter' && searchTerm.trim() && filteredOptions.length > 0) {
                e.preventDefault();
                onValueChange(filteredOptions[0].name);
                setOpen(false);
              }
            }}
            className="h-8 text-[13px] placeholder:text-heritage-grey"
            autoComplete="off"
          />
        </div>
        <div
          className="max-h-60 overflow-y-auto overscroll-contain py-1"
          role="listbox"
          aria-label={placeholder}
          onWheel={(e) => e.stopPropagation()}
        >
          {filteredOptions.length === 0 ? (
            <p className="px-3 py-3 text-center text-[13px] text-heritage-grey">
              {searchTerm.trim() ? 'No results found' : 'No options available'}
            </p>
          ) : (
            filteredOptions.map((option) => {
              const selected = value === option.name;
              return (
                <button
                  key={String(option.id)}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    onValueChange(option.name);
                    setOpen(false);
                  }}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2 text-[13px] text-left transition-colors hover:bg-page-bg',
                    selected ? 'text-blueprint-blue font-medium bg-page-bg' : 'text-text-grey'
                  )}
                >
                  <Check
                    className={cn('h-4 w-4 shrink-0', selected ? 'text-hero-red' : 'opacity-0')}
                  />
                  <span className="truncate">{option.name}</span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
